import { z } from "zod";
import { defineStepConfig } from "../../registry";

/**
 * Why a requested snooze was refused. Shared vocabulary across the snooze
 * service's return value, this step's output socket, the agent tool result,
 * and the exhaustion notification payload — so a reason never has to be
 * re-derived or re-worded as it crosses a boundary.
 */
export const RerunLimitReasonSchema = z.enum(["max_reruns", "expired"]);
export type RerunLimitReason = z.infer<typeof RerunLimitReasonSchema>;

/**
 * How much of the prior run the scheduled rerun re-executes. Shared vocabulary
 * between this step's config and the snooze service. See the config field's
 * description for the semantics of each value.
 */
export const RerunScopeSchema = z.enum([
  "replay_after_last_fanout",
  "replay_after_trigger_step",
  "full",
]);
export type RerunScope = z.infer<typeof RerunScopeSchema>;

/**
 * Snooze Workflow Rerun Step
 *
 * Cancels the current workflow run and schedules the same workflow definition
 * to be re-dispatched against the same subject entity after `delayMinutes`.
 *
 * `maxReruns` / `expiresAfterMinutes` bound the chain so a workflow whose
 * upstream data never arrives stops rerunning instead of looping forever. Both
 * are evaluated against the ROOT original run, and whichever trips first wins.
 * When a limit refuses the snooze the step reports `scheduled: false` with a
 * `limitReason`, and the snooze service notifies the workflow's owner and
 * watchers.
 *
 * Shared tool/step: the same config backs both the deterministic workflow node
 * (branched to when upstream data isn't ready) and the AI agent tool
 * (invoked when the agent determines upstream sync hasn't caught up).
 *
 * Introduced as a temporary primitive alongside GEN-1861 (Famous status hack);
 * it remains generic — any workflow can branch to this step to defer itself.
 */
export const snoozeWorkflowRerunStep = defineStepConfig({
  type: "snooze_workflow_rerun",
  name: "Snooze Workflow Rerun",
  description:
    "Cancel the current workflow run and schedule the same workflow to rerun after a delay. Useful when upstream data isn't yet ready to process.",
  category: "Control Flow",
  inputSchema: z.object({
    reason: z
      .string()
      .describe(
        "Why the workflow is being snoozed (surfaced in logs/analytics)",
      ),
    unreadyReferences: z
      .array(
        z.object({
          type: z.string(),
          id: z.string(),
          currentStatus: z.string().nullable(),
        }),
      )
      .optional()
      .describe("Optional structured context about what wasn't ready"),
  }),
  outputSchema: z.object({
    scheduled: z.boolean(),
    scheduledFor: z
      .string()
      .nullable()
      .describe(
        "ISO timestamp of the scheduled rerun, or null when the snooze was refused",
      ),
    limitReason: RerunLimitReasonSchema.nullable().describe(
      "Why the snooze was refused, when `scheduled` is false. Null on success.",
    ),
  }),
  configSchema: z.object({
    delayMinutes: z
      .number()
      .int()
      .min(1)
      .max(10080)
      .describe("How long to wait before re-dispatching the workflow"),
    maxReruns: z
      .number()
      .int()
      .min(1)
      .max(1000)
      .optional()
      .describe(
        "Stop rerunning after this many automatic reruns of the original run. Unlimited when omitted.",
      ),
    expiresAfterMinutes: z
      .number()
      .int()
      .min(1)
      .optional()
      .describe(
        "Stop rerunning once this much time has elapsed since the ORIGINAL run was triggered. Measured from the root so the window doesn't slide with each rerun. Unlimited when omitted.",
      ),
    versionStrategy: z
      .enum(["latest_published", "pinned"])
      .default("latest_published")
      .describe(
        "Which definition version the rerun runs. `latest_published` picks up edits published since the original run — including a rerun limit added mid-chain, which a pinned chain would never see. `pinned` replays the exact version the original started on.",
      ),
    onLimitReached: z
      .enum(["stop", "fail"])
      .default("stop")
      .describe(
        "What to do once a limit is hit: `stop` ends the run quietly, `fail` surfaces it as a failed run.",
      ),
    rerunScope: RerunScopeSchema.default("replay_after_last_fanout").describe(
      "How much of the prior run the rerun re-executes. `replay_after_last_fanout` (default) reuses every step result the prior run computed before its fanout and re-executes only the fanout and downstream — the per-item work that snoozed. `replay_after_trigger_step` reuses everything upstream of the step that requested the snooze (degrades to the fanout cut when that step ran inside a fanout child). `full` re-executes the entire workflow from the trigger — expensive: every step, including AI/agent work, runs again. Replay requires the rerun to use the same definition version as the prior run; when a new version was published mid-chain the rerun falls back to `full` automatically.",
    ),
  }),
  emits: [] as const,
});
